import { inject, observer } from 'mobx-react';
import * as React from 'react';

/* eslint-disable max-lines */
import { AnalyticsStore, RouteStore } from 'bernie-core';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { ViewExtraLarge, ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import { PropertyRoomScoreInfoModel, RoomViewModel } from 'models';

import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkCardAloha, UitkCardContent, UitkCardSectionSeparatorType, UitkCardTextSegment } from 'uitk-react-cards';
import { UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';

import { addIcon } from 'utils/amenity-mapper';

import { BeddingOptions } from './bedding-options';
import { ChooseDatesCTA, ChooseDatesCTAProps } from './room/internals/choose-dates-cta';
import { GuestLikeItForList } from './guest-like-it-for-list';
import { GuestRoomScoreItem } from './guest-room-score-item';
import { PropertyInfoSection } from 'components/infosite/property-information/property-info-section';
import { RoomMaxOccupancySegment } from 'components/infosite/rooms-and-rates/room-info-dialog-occupancy';
import { RoomPhotosSegment } from 'components/infosite/rooms-and-rates/room-photos-segment';
import { RoomRatesSegment } from 'components/infosite/rooms-and-rates/room-rates-segment';
import { RoomTitle } from 'components/infosite/rooms-and-rates/room-title';
import { SpacesWrapper } from 'components/infosite/property-space-details';

export interface RoomInfoDialogProps extends LocalizedComponentProps {
  action: () => void;
  analytics: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  excludeReserveButton?: boolean;
  position: string;
  room: RoomViewModel;
  trackingRfrr?: string;
  routeStore?: RouteStore;
  excludePriceBreakDownLink?: boolean;
}

@withLocalization
@inject('currentHotel')
@observer
export class RoomInfoDialog extends React.Component<RoomInfoDialogProps> {
  public render() {
    const {
      action,
      analytics,
      currentHotel,
      excludeReserveButton,
      l10n,
      position,
      room,
      trackingRfrr,
      routeStore,
      excludePriceBreakDownLink,
    } = this.props;

    currentHotel.inRoomInfoDialog = true;

    const { beddingOptions, description, maxOccupancy, name, roomTypeCode, spacesViewModel, view } = room;
    const { formatText } = l10n;

    const sectionLabels = currentHotel && currentHotel.offers && currentHotel.offers.sectionLabels;
    const dialogProps = {
      className: 'room-info-dialog',
      dialogShow: true,
    };

    const toolbarAnalytics = analytics.setupTracker(trackingRfrr, 'Close Room Info dialog');

    const headerTitle: string = (sectionLabels && sectionLabels.roomInfo) || formatText('hotels.infosite.roomInfoDialog.title');

    const toolBarAttributes = {
      header: headerTitle,
      iconLabel: formatText('hotels.infosite.roomInfo.cancelButton.a11yLabel'),
      type: ToolbarType.CLOSE,
    };

    const titleDescription: string = view ? view.description : '';

    const roomDetailsTitle: string =
      (sectionLabels && sectionLabels.roomDetails) || formatText('hotels.infosite.roomInfoDialog.roomDetails');

    const roomAmenities = room?.roomAmenities;
    const roomAmenitiesSection = room?.roomAmenitiesSection;
    const hasRoomAmenitiesSection = Boolean(roomAmenitiesSection && Object.keys(roomAmenitiesSection).length > 0);

    const roomGallerySegment = <RoomPhotosSegment analytics={analytics} l10n={l10n} position={position} room={room} />;

    const roomAmenetiesSegment = (
      <>
        {roomAmenitiesSection && (
          <UitkCardTextSegment className="all-y-gutter-zero">
            <PropertyInfoSection propertyInfoSection={roomAmenitiesSection} />
          </UitkCardTextSegment>
        )}
      </>
    );

    const roomRatesSegment = (
      <>
        <h2 className="room-options all-b-padding-six uitk-type-heading-600">
          <LocalizedText message="hotels.infosite.roomInfoDialog.roomOption.heading" />
        </h2>

        <RoomRatesSegment
          position={position}
          room={room}
          excludeReserveButton={excludeReserveButton}
          rffr=".RoomInfoDialog"
          excludePriceBreakDownLink={excludePriceBreakDownLink}
          layerId="room-info-dialog"
        />
      </>
    );

    const roomDetailsSegment = (
      <UitkCardTextSegment data-stid="room-info-description">
        <ul className={'uitk-grid no-bullet uitk-type-300 pwa-theme--grey-700'}>
          {roomAmenities &&
            roomAmenities.map((amenity, index) => {
              const amenityWithIcon = amenity.icon ? amenity : addIcon(amenity);

              return (
                <li className={`uitk-cell all-t-margin-two`} key={`amenity-${index}-${amenityWithIcon.description}`}>
                  <UitkIcon id={`amenity-icon-${amenityWithIcon.icon}`} name={amenityWithIcon.icon} size={UitkIconSize.MEDIUM} />
                  <span className="amenities-icon-description all-l-padding-two">{amenityWithIcon.description}</span>
                </li>
              );
            })}
        </ul>
      </UitkCardTextSegment>
    );

    const roomScoreInfo = currentHotel.getRoomScore(roomTypeCode);
    const guestRoomScoreSegment = roomScoreInfo ? <GuestRoomScoreSegment roomScoreInfo={roomScoreInfo} /> : null;

    const roomTitleSegment = <RoomTitleSegment description={titleDescription} name={name} />;
    const ctaDetailsSegment = (
      <>{currentHotel.searchCriteria.isDateless && <CTADetailsSegment hotelPosition={position} routeStore={routeStore} />}</>
    );
    const smallView = (
      <div className="all-x-padding-three all-t-margin-three">
        <UitkCardAloha>
          <UitkCardContent>{roomGallerySegment}</UitkCardContent>
        </UitkCardAloha>
        <div className="all-t-margin-six">
          {roomTitleSegment}
          {roomDetailsSegment}
          {guestRoomScoreSegment}
          {roomAmenetiesSegment}
          {roomRatesSegment}
          {ctaDetailsSegment}
          <RoomSleepingArrangementsSegment analytics={analytics} spacesViewModel={spacesViewModel} />
        </div>
      </div>
    );

    const mediumView = (
      <div className="all-x-padding-twelve all-t-margin-six">
        <UitkCardAloha>
          <UitkCardContent>{roomGallerySegment}</UitkCardContent>
        </UitkCardAloha>
        <div className="all-t-margin-six">
          {roomTitleSegment}
          {roomDetailsSegment}
          {guestRoomScoreSegment}
          {roomAmenetiesSegment}
          {roomRatesSegment}
          {ctaDetailsSegment}
          <RoomSleepingArrangementsSegment analytics={analytics} spacesViewModel={spacesViewModel} />
        </div>
      </div>
    );

    const largeView = (
      <UitkLayoutGrid columns={3} className="all-x-padding-twelve">
        <UitkLayoutGridItem>
          <div className="all-t-margin-three">
            {roomTitleSegment}
            {roomDetailsSegment}
            {guestRoomScoreSegment}
            {roomAmenetiesSegment}
            <RoomSleepingArrangementsSegment analytics={analytics} spacesViewModel={spacesViewModel} />
          </div>
        </UitkLayoutGridItem>
        <UitkLayoutGridItem colSpan={2}>
          <div className="all-t-margin-six">
            <UitkCardAloha>
              <UitkCardContent>{roomGallerySegment}</UitkCardContent>
            </UitkCardAloha>
            <div className="all-t-margin-six">
              {roomRatesSegment}
              {ctaDetailsSegment}
            </div>
          </div>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );

    const extraLargeView = (
      <UitkLayoutGrid columns={3}>
        <UitkLayoutGridItem>
          <div className="all-t-margin-three">
            {roomTitleSegment}
            {roomDetailsSegment}
            {guestRoomScoreSegment}
            {roomAmenetiesSegment}
            <RoomSleepingArrangementsSegment analytics={analytics} spacesViewModel={spacesViewModel} />
          </div>
        </UitkLayoutGridItem>
        <UitkLayoutGridItem colSpan={2}>
          <div className="all-t-margin-six">
            <UitkCardAloha>
              <UitkCardContent>{roomGallerySegment}</UitkCardContent>
            </UitkCardAloha>
            <div className="all-t-margin-six">
              {roomRatesSegment}
              {ctaDetailsSegment}
            </div>
          </div>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );

    const controlledExperience = (
      <UitkFullscreenDialog {...dialogProps}>
        <UitkToolbar {...toolBarAttributes} action={action} analytics={toolbarAnalytics} />
        <UitkDialogContent>
          <div>
            <RoomPhotosSegment analytics={analytics} l10n={l10n} position={position} room={room} />
            <div className="all-x-padding-four all-t-padding-six">
              <RoomTitleSegment description={titleDescription} name={name} />
              {guestRoomScoreSegment}
              <BeddingOptions options={beddingOptions} />
              <RoomSleepingArrangementsSegment analytics={analytics} spacesViewModel={spacesViewModel} />
              <RoomMaxOccupancySegment maxOccupancy={maxOccupancy} />
              <RoomDetailsSegment description={description} label={roomDetailsTitle} />
              <RoomRatesSegment
                position={position}
                room={room}
                excludeReserveButton={excludeReserveButton}
                rffr=".RoomInfoDialog"
                excludePriceBreakDownLink={excludePriceBreakDownLink}
                layerId="room-info-dialog"
              />
              {currentHotel.searchCriteria.isDateless && <CTADetailsSegment hotelPosition={position} routeStore={routeStore} />}
            </div>
          </div>
        </UitkDialogContent>
      </UitkFullscreenDialog>
    );

    return (
      <>
        {hasRoomAmenitiesSection ? (
          <UitkFullscreenDialog {...dialogProps} strictFullscreen={true} wideContent={true}>
            <UitkToolbar {...toolBarAttributes} action={action} analytics={toolbarAnalytics} />
            <UitkDialogContent>
              <Viewport>
                <ViewSmall>{smallView}</ViewSmall>
                <ViewMedium>{mediumView}</ViewMedium>
                <ViewLarge>{largeView}</ViewLarge>
                <ViewExtraLarge>{extraLargeView}</ViewExtraLarge>
              </Viewport>
            </UitkDialogContent>
          </UitkFullscreenDialog>
        ) : (
          <>{controlledExperience}</>
        )}
      </>
    );
  }
}

interface RoomTitleSegmentProps {
  description: string;
  name: string;
}

const RoomTitleSegment = ({ description, name }: RoomTitleSegmentProps) => {
  if (!description && !name) {
    return null;
  }

  return (
    <UitkCardTextSegment className="all-y-gutter-zero" data-stid="room-info-title">
      <RoomTitle name={name} description={description} />
    </UitkCardTextSegment>
  );
};

const RoomSleepingArrangementsSegment = ({ analytics, spacesViewModel }) => {
  if (!spacesViewModel) {
    return null;
  }

  return (
    <UitkCardTextSegment data-stid="room-info-sleeping-arrangements">
      <SpacesWrapper analytics={analytics} isDialog={true} spacesViewModel={spacesViewModel} />
    </UitkCardTextSegment>
  );
};

interface GuestRoomScoreSegmentProps {
  roomScoreInfo: PropertyRoomScoreInfoModel;
}

const GuestRoomScoreSegment = ({ roomScoreInfo }: GuestRoomScoreSegmentProps) => {
  if (!roomScoreInfo) {
    return null;
  }

  return (
    <UitkCardTextSegment data-stid="room-info-guest-room-rating">
      {roomScoreInfo.score && (
        <h3 className="uitk-type-heading-400 all-b-margin-three" data-stid="room-info-guest-room-rating-score">
          <LocalizedText message="hotels.infosite.roomInfoDialog.roomDetails.roomscore.mainText" data={[roomScoreInfo.score]} />
        </h3>
      )}
      {roomScoreInfo.tags && roomScoreInfo.tags.length > 0 && (
        <ul className="no-bullet" data-stid="room-info-guest-room-rating-list">
          <GuestRoomScoreItem roomScoreCriteriaAndCount="hotels.infosite.roomInfoDialog.roomDetails.roomscore.detailText" isBold={true} />
          <GuestLikeItForList guestLikeItForTags={roomScoreInfo.tags} />
        </ul>
      )}
    </UitkCardTextSegment>
  );
};

interface RoomDetailsSegmentProps {
  description: string;
  label: string;
}

const RoomDetailsSegment = ({ description, label }: RoomDetailsSegmentProps) => {
  if (!description) {
    return null;
  }

  return (
    <UitkCardTextSegment data-stid="room-info-description" separator={UitkCardSectionSeparatorType.BOTTOM}>
      <h3 className="uitk-type-heading-400 all-b-margin-three">{label}</h3>
      <div className="predefined" dangerouslySetInnerHTML={{ __html: description }} />
    </UitkCardTextSegment>
  );
};

const CTADetailsSegment = ({ hotelPosition, routeStore }: ChooseDatesCTAProps) => (
  <UitkCardTextSegment data-stid="room-info-cta">
    <ChooseDatesCTA hotelPosition={hotelPosition} routeStore={routeStore} dialog={true} />
  </UitkCardTextSegment>
);
